import React, { useRef } from 'react';
import FeaturedBlocks from '../components/FeaturedBlocks';
import LatestPhotosBlock from '../components/LatestPhotosBlock';
import Seo from '../components/Seo';
import { ChevronDown } from 'lucide-react';

const R2_PUBLIC_URL = 'https://media.explora.mu';

const HomePage = () => {
  const photosRef = useRef(null);

  const scrollToPhotos = () => {
    photosRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="home-page bg-white">
      <Seo
        title="Mauritius Photos by Local Photographers"
        description="Discover beautiful, high-resolution Mauritius photography — beaches, hotels, nature, wildlife and culture captured by talented local photographers."
        image={`${R2_PUBLIC_URL}/assets/background-explora.webp`}
        imageAlt="Mauritius landscape photography"
      />
      {/* Hero preloads (boom font + mobile/desktop hero image) are injected
          early, homepage-only, by an inline script in public/index.html so
          they're discovered during initial HTML parse — see index.html. */}
      {/* Hero Section with Background Image - Full viewport height */}
      <div
        className="home-hero-bg relative bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{
          marginTop: '-64px',
          height: 'calc(100vh + 64px)'
        }}
      >
        {/* Hero Content */}
        <div className="relative z-10 flex flex-col items-center justify-center h-full pt-20 pb-20">
          <div className="max-w-xl text-center px-4">
            <h1 className="home-hero-title text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              <span className="home-hero-title-main">Explore Mauritius</span>{' '}
              <span className="home-hero-title-sub">through Photography</span>
            </h1>
            <p className="text-base md:text-lg text-white/90 leading-relaxed">
              Discover stunning landscapes, nature, and island life from Mauritius, captured by talented local photographers and shared on Explora.
            </p>
          </div>

          {/* Tags Section */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 max-w-xl px-4">
            <span className="text-sm font-medium text-white/80 mr-2">Trending:</span>
            <a
              href="/photos-hd/beaches-of-mauritius"
              className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-sm rounded-md transition-colors backdrop-blur-sm"
            >
              beaches
            </a>
            <a
              href="/photos-hd/mauritius-hotels"
              className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-sm rounded-md transition-colors backdrop-blur-sm"
            >
              hotels
            </a>
            <a
              href="/photos-hd/forest-and-nature-parks"
              className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-sm rounded-md transition-colors backdrop-blur-sm"
            >
              nature
            </a>
            <a
              href="/photos-hd/waterfalls"
              className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-sm rounded-md transition-colors backdrop-blur-sm"
            >
              waterfalls
            </a>
            <a
              href="/photos-hd/sunsets-of-mauritius"
              className="px-3 py-1 bg-white/20 hover:bg-white/30 text-white text-sm rounded-md transition-colors backdrop-blur-sm"
            >
              sunsets
            </a>
          </div>
        </div>

        {/* Scroll Down Button - Yellow bar with rounded top corners, positioned at bottom inside hero */}
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-10 w-full max-w-xl px-4">
          <button
            onClick={scrollToPhotos}
            className="w-full py-2 flex items-center justify-center hover:opacity-90 rounded-t-xl"
            style={{ backgroundColor: '#CFD700' }}
            aria-label="Scroll to featured content"
          >
            <ChevronDown className="w-6 h-6 text-black" />
          </button>
        </div>
      </div>

      {/* Admin-managed Featured Blocks */}
      <div ref={photosRef} className="home-blocks" data-testid="home-blocks">
        <FeaturedBlocks />
        <LatestPhotosBlock />
      </div>
    </div>
  );
};

export default HomePage;
